import type { TFunction } from 'i18next';
import { get, getTodos } from '../../nucleo/api';
import { ListChecks, TrendingUp } from 'lucide-react';
import { dataHora, dinheiro, nomeParceiro, nomePessoa, numero, textoLivre } from '../../nucleo/formato';
import type { FonteBuscaGlobal, PerguntaCopiloto, RespostaCopiloto } from '../../nucleo/modulos-web';
import { rotuloEtapaPipeline } from '../../nucleo/rotulos';
import { CAMINHO_COMERCIAL, CAMINHO_CONFIGURACOES, caminhoDaNegociacao } from './crm-caminhos';

/**
 * CRM no novo desenho, lote 6 (N8 e N9): o que o Pipeline põe na busca da barra superior (negociações) e as perguntas que ele
 * responde no Copiloto, com respostas simuladas a partir das oportunidades (a mesma API e as mesmas permissões das telas).
 */
interface Oportunidade {
  id: string;
  titulo: string;
  parceiro_nome: string | null;
  etapa_nome: string;
  funil_nome: string;
  responsavel_nome: string | null;
  origem: string;
  valor_centavos: number;
  moeda: string;
  estado: 'aberta' | 'ganha' | 'perdida';
}
interface EtapaQuadro {
  id: string;
  nome: string;
  tipo: 'aberta' | 'ganha' | 'perdida';
  oportunidades: number;
}

/** Rodada crm1 (SPEC-M1): a busca e o Copiloto levam às páginas novas do CRM (a negociação abre a ficha em página). */
const CONFIG_FUNIS = `${CAMINHO_CONFIGURACOES}/funis`;
const CONFIG_MOTIVOS = `${CAMINHO_CONFIGURACOES}/motivos`;

/** Até 2.000 oportunidades (10 páginas), guardadas por 30 s: as perguntas seguidas não refazem a leitura. */
let cache: { em: number; dados: Promise<Oportunidade[]> } | null = null;
function oportunidades(): Promise<Oportunidade[]> {
  if (!cache || Date.now() - cache.em > 30_000) {
    const dados = getTodos<Oportunidade>('/pipeline/oportunidades', {}, 10).then((r) => r.itens);
    dados.catch(() => (cache = null));
    cache = { em: Date.now(), dados };
  }
  return cache.dados;
}

/** Contagem por chave, da maior para a menor (empate pelo nome). */
function contar<T>(lista: T[], chave: (x: T) => string): [string, T[]][] {
  const m = new Map<string, T[]>();
  for (const x of lista) m.set(chave(x), [...(m.get(chave(x)) ?? []), x]);
  return [...m.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
}
const somar = (l: Oportunidade[]) => l.reduce((a, o) => a + o.valor_centavos, 0);
const moedaDe = (l: Oportunidade[]) => l[0]?.moeda ?? 'BRL';
const pct = (parte: number, total: number) => (total ? `${numero((parte / total) * 100, 0)}%` : '0%');

interface AtividadeBusca {
  id: string;
  titulo: string;
  oportunidade_id: string;
  oportunidade_titulo: string;
  parceiro_nome: string | null;
  data_hora: string;
}

/**
 * Lista de 30/10 (item 10): as linhas vêm do servidor (`/busca-global`, que roda as mesmas rotas de listagem com `busca` e
 * `limite`). A atividade abre a negociação dela (a ficha em página mostra as atividades).
 */
export const buscaPipeline: FonteBuscaGlobal[] = [
  {
    chave: 'negociacoes',
    permissao: 'pipeline.ver',
    pagina: 'pipeline.pipeline',
    grupo: 'crm.buscaNegociacoes',
    posicao: 10,
    icone: TrendingUp,
    item: (o: Oportunidade) => ({
      id: o.id,
      // Rodada crm1 (VIS-B3): o título passa pelo modo discreto como nas telas do CRM (nome do cliente vira iniciais).
      rotulo: textoLivre(o.titulo, o.parceiro_nome),
      // Lista de 30/10 (item 10): a etapa de fábrica no idioma da tela, sem mudar o valor gravado.
      detalhe: [rotuloEtapaPipeline(o.etapa_nome), o.parceiro_nome && nomeParceiro(o.parceiro_nome)].filter(Boolean).join(' · '),
      link: caminhoDaNegociacao(o.id),
    }),
  },
  {
    chave: 'atividades',
    permissao: 'pipeline.ver',
    pagina: 'pipeline.atividades',
    grupo: 'buscaGlobal.atividades',
    posicao: 15,
    icone: ListChecks,
    item: (a: AtividadeBusca) => ({
      id: a.id,
      rotulo: textoLivre(a.titulo, a.parceiro_nome),
      detalhe: [textoLivre(a.oportunidade_titulo, a.parceiro_nome), dataHora(a.data_hora)].filter(Boolean).join(' · '),
      link: caminhoDaNegociacao(a.oportunidade_id),
    }),
  },
];

const responder = (fn: (t: TFunction) => Promise<RespostaCopiloto>) => fn;

export const copilotoPipeline: PerguntaCopiloto[] = [
  {
    chave: 'etapas',
    categoria: 'analise',
    permissao: 'pipeline.ver',
    pergunta: 'crm.copiloto.etapas',
    casa: /(cada|por) etapa|etapas do funil|em andamento/,
    responder: responder(async (t) => {
      const q = await get<{ funil: { nome: string }; etapas: EtapaQuadro[] }>('/pipeline/quadro');
      const abertas = q.etapas.filter((e) => e.tipo === 'aberta');
      return {
        texto: t('crm.copiloto.etapasResposta', { funil: q.funil.nome, count: abertas.reduce((a, e) => a + e.oportunidades, 0) }),
        itens: abertas.map((e) => ({ rotulo: e.nome, valor: numero(e.oportunidades, 0) })),
        link: { rotulo: t('crm.copiloto.abrirQuadro'), para: CAMINHO_COMERCIAL },
      };
    }),
  },
  {
    chave: 'resultado',
    categoria: 'analise',
    permissao: 'pipeline.ver',
    pergunta: 'crm.copiloto.resultado',
    casa: /criadas|vendidas|ganhas|perdidas e|resultado/,
    responder: responder(async (t) => {
      const l = await oportunidades();
      const por = (e: Oportunidade['estado']) => l.filter((o) => o.estado === e);
      return {
        texto: t('crm.copiloto.resultadoResposta'),
        itens: [
          { rotulo: t('crm.copiloto.criadas'), valor: numero(l.length, 0) },
          { rotulo: t('crm.copiloto.emAndamento'), valor: numero(por('aberta').length, 0) },
          { rotulo: t('crm.copiloto.vendidas'), valor: `${numero(por('ganha').length, 0)} (${dinheiro(somar(por('ganha')), moedaDe(l))})` },
          { rotulo: t('crm.copiloto.perdidas'), valor: numero(por('perdida').length, 0) },
        ],
      };
    }),
  },
  {
    chave: 'equipe',
    categoria: 'analise',
    permissao: 'pipeline.ver',
    pergunta: 'crm.copiloto.equipe',
    casa: /desempenho|equipe|vendedor|responsave/,
    responder: responder(async (t) => {
      const l = await oportunidades();
      return {
        texto: t('crm.copiloto.equipeResposta'),
        itens: contar(l, (o) => o.responsavel_nome ?? t('crm.copiloto.semResponsavel')).map(([nome, m]) => {
          const ganhas = m.filter((o) => o.estado === 'ganha');
          return { rotulo: nomePessoa(nome), valor: t('crm.copiloto.equipeLinha', { total: numero(m.length, 0), ganhas: numero(ganhas.length, 0), valor: dinheiro(somar(ganhas), moedaDe(m)) }) };
        }),
      };
    }),
  },
  {
    chave: 'origem',
    categoria: 'analise',
    permissao: 'pipeline.ver',
    pergunta: 'crm.copiloto.origem',
    casa: /fonte|origem|conversao/,
    responder: responder(async (t) => {
      const l = await oportunidades();
      const linhas = contar(l, (o) => o.origem || 'manual')
        .map(([origem, m]) => ({ origem, m, taxa: m.filter((o) => o.estado === 'ganha').length / m.length }))
        .sort((a, b) => b.taxa - a.taxa);
      return {
        texto: linhas[0] ? t('crm.copiloto.origemResposta', { origem: linhas[0].origem }) : t('crm.copiloto.semDados'),
        itens: linhas.map(({ origem, m }) => ({ rotulo: origem, valor: t('crm.copiloto.origemLinha', { taxa: pct(m.filter((o) => o.estado === 'ganha').length, m.length), count: m.length }) })),
      };
    }),
  },
  // Rodada crm2 (SPEC-B1): o link vai para as Configurações do CRM, que pedem pipeline.configurar (val5).
  {
    chave: 'funis',
    categoria: 'duvidas',
    permissao: 'pipeline.configurar',
    pergunta: 'crm.copiloto.funis',
    casa: /(cri|edit|mud)\w* .*funi|funis de venda|novo funil/,
    responder: responder(async (t) => ({ texto: t('crm.copiloto.funisResposta'), link: { rotulo: t('crm.copiloto.abrirFunis'), para: CONFIG_FUNIS } })),
  },
  {
    chave: 'motivos',
    categoria: 'duvidas',
    permissao: 'pipeline.configurar',
    pergunta: 'crm.copiloto.motivos',
    casa: /motivos? de perda/,
    responder: responder(async (t) => ({ texto: t('crm.copiloto.motivosResposta'), link: { rotulo: t('crm.copiloto.abrirMotivos'), para: CONFIG_MOTIVOS } })),
  },
  {
    chave: 'perdas',
    categoria: 'analise',
    permissao: 'pipeline.ver',
    pergunta: 'crm.copiloto.perdas',
    casa: /perdendo|perda|perdemos/,
    responder: responder(async (t) => {
      const perdidas = (await oportunidades()).filter((o) => o.estado === 'perdida');
      if (!perdidas.length) return { texto: t('crm.copiloto.semPerdas') };
      return {
        texto: t('crm.copiloto.perdasResposta', { count: perdidas.length }),
        itens: contar(perdidas, (o) => o.etapa_nome).map(([etapa, m]) => ({ rotulo: etapa, valor: t('crm.copiloto.perdidasN', { count: m.length }) })),
      };
    }),
  },
];
